"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Capabilities() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const services = [{
    number: "01",
    title: "AI THREAT DETECTION",
    path: "/AIThreatDetectionPage",
    descriptor: "Intelligent monitoring to identify and neutralize threats in real-time.",
    detail: "Our AI-driven engines analyze network traffic patterns to detect anomalies before they escalate. By leveraging machine learning, we identify zero-day vulnerabilities and sophisticated attack vectors, ensuring your digital perimeter remains impenetrable against evolving cyber threats.",
    specs: ["Real-time anomaly detection", "Machine learning threat modeling", "Automated signature updates", "Comprehensive threat reporting"]
  }, {
    number: "02",
    title: "24/7 SECURITY MONITORING",
    path: "/SecurityMonitoringPage",
    descriptor: "Continuous surveillance of your digital assets around the clock.",
    detail: "Cyber threats never sleep, and neither do we. Our Security Operations Center provides constant oversight of your infrastructure, ensuring that any suspicious activity is flagged, investigated, and mitigated immediately by our expert security analysts.",
    specs: ["Round-the-clock SOC support", "Instant incident notification", "Global threat intelligence feeds", "Proactive system health checks"]
  }, {
    number: "03",
    title: "PHISHING PREVENTION",
    path: "/PhishingPreventionPage",
    descriptor: "Advanced defense against social engineering and email-based attacks.",
    detail: "We implement robust email filtering and authentication protocols to block malicious phishing attempts. Our solutions protect your employees from deceptive communications, significantly reducing the risk of credential theft and unauthorized system access.",
    specs: ["Advanced email filtering", "DMARC/SPF/DKIM implementation", "Automated link analysis", "User-targeted threat blocking"]
  }, {
    number: "04",
    title: "VULNERABILITY ASSESSMENTS",
    path: "/VulnerabilityAssessmentsPage",
    descriptor: "Systematic identification of security gaps within your infrastructure.",
    detail: "We conduct deep-dive assessments to uncover hidden weaknesses in your software and network configurations. Our detailed reports provide actionable insights, allowing you to patch vulnerabilities before they can be exploited by malicious actors.",
    specs: ["Automated vulnerability scanning", "Manual penetration testing", "Compliance-focused audits", "Prioritized remediation plans"]
  }, {
    number: "05",
    title: "INCIDENT RESPONSE",
    path: "/IncidentResponsePage",
    descriptor: "Rapid, expert intervention to contain and resolve security breaches.",
    detail: "In the event of a security incident, our rapid response team is ready to act. We focus on containment, forensic analysis, and recovery, minimizing downtime and ensuring your business operations return to normal as quickly and securely as possible.",
    specs: ["Emergency response protocols", "Digital forensic investigation", "Data recovery assistance", "Post-incident impact analysis"]
  }];
  return <section data-section-id="4236" id="comprehensive-protection" className="bg-background text-foreground py-40 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 mb-24 lg:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-3">
            <div className="font-mono text-xs tracking-widest text-primary mb-8 uppercase">
              01 / CAPABILITIES
            </div>
            <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[6rem]">
              Our Security
              <br />
              Expertise
            </h2>
          </div>
          <div className="lg:col-span-2 flex items-end">
            <p className="font-default text-xl md:text-2xl leading-relaxed text-muted-foreground">
              InfinitySecure provides a comprehensive suite of AI-powered cybersecurity solutions. From proactive threat detection to rapid incident response, we ensure your business remains resilient in an increasingly complex digital landscape.
            </p>
          </div>
        </div>

        <div ref={listRef} className={`border-t border-border transition-all duration-1000 ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {services.map((service, i) => {
          const isOpen = openIndex === i;
          return <div key={i} data-index={i} className={`border-b border-border transition-all duration-500 ${isOpen ? "bg-card/40 backdrop-blur-sm" : ""}`}>
                <Button onClick={() => setOpenIndex(isOpen ? null : i)} variant="ghost" className="w-full group py-8 md:py-10 px-2 md:px-6 grid grid-cols-12 gap-4 md:gap-8 items-center text-left h-auto justify-start" aria-expanded={isOpen}>
                  <div className="col-span-2 md:col-span-1">
                    <span className="font-default font-bold text-3xl md:text-5xl text-transparent" style={{
                  WebkitTextStroke: `1px ${isOpen ? "var(--primary)" : "var(--foreground)"}`
                }}>
                      {service.number}
                    </span>
                  </div>

                  <div className="col-span-10 md:col-span-6">
                    <h3 className={`font-default font-bold uppercase tracking-tight text-2xl md:text-4xl lg:text-5xl transition-colors duration-500 ${isOpen ? "text-primary" : "text-foreground group-hover:text-primary"}`}>
                      <Link to={service.path} className="hover:underline decoration-primary/30 underline-offset-8">
                        {service.title}
                      </Link>
                    </h3>
                  </div>

                  <div className="hidden md:block md:col-span-4">
                    
                  </div>

                  <div className="col-span-12 md:col-span-1 flex md:justify-end">
                    <div className={`inline-flex items-center justify-center size-10 md:size-12 border border-border transition-all duration-500 ${isOpen ? "rotate-45 border-primary text-primary" : "text-foreground group-hover:border-primary group-hover:text-primary"}`} aria-hidden="true">
                      <Plus className="size-5 md:size-6" strokeWidth={1.5} />
                    </div>
                  </div>
                </Button>

                <div className={`grid transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="px-2 md:px-6 pb-12 pt-2 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                      <div className="lg:col-span-1" />
                      <div className="lg:col-span-6">
                        <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/90">
                          {service.detail}
                        </p>
                        <div className="mt-8">
                          <Link to={service.path}>
                            <Button variant="link" className="px-0 font-default font-medium tracking-widest uppercase text-sm text-primary hover:text-primary/80">
                              Learn More &rarr;
                            </Button>
                          </Link>
                        </div>
                      </div>
                      <div className="lg:col-span-5">
                        <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-4">
                          Key Features
                        </div>
                        <ul className="space-y-3">
                          {service.specs.map((spec, j) => <li key={j} data-index={j} className="flex items-start gap-3 font-default text-base md:text-lg text-foreground/90">
                              <span className="mt-2 size-1.5 shrink-0 bg-primary" aria-hidden="true" />
                              <span>{spec}</span>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}